import { FC, useState, useCallback, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { format, parseISO, subDays } from 'date-fns';
import { AuthSession, AccountContext } from 'auth/accountProvider';
import {
  UploadErrorDisplay,
  DetectedFileTypeDropdown,
  UploadFileMetadata,
  UploadProgress,
  UploadError,
  UploadResponse,
  FileDetectionResult,
  DetectedFileType,
  parseUploadError,
  FILE_TYPE_LABELS,
  CONFIDENCE_STYLES,
} from 'components/lpiUpload';
import IngestionIssuesTable from '../cpiReports/IngestionIssuesTable';
import UploadHistoryTab from './UploadHistoryTab';
import DatasetsTab from './DatasetsTab';
import DateRangeDropdown from '../lpiReports/DateRangeDropdown';
import './LpiUploads.scss';

type UploadTab = 'upload-data' | 'upload-history' | 'ingestion-issues' | 'datasets';

const parseDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  const date = parseISO(dateStr);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateToString = (date: Date | null): string => {
  if (!date) return '';
  return format(date, 'yyyy-MM-dd');
};

const ALLOWED_EXTENSIONS = ['csv', 'xlsx', 'xls'];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes for large files

// Helper to safely get session tokens with validation
interface SessionTokens {
  jwtToken: string;
  sessionId: string;
}

const getSessionTokens = async (
  getSession: () => Promise<AuthSession>
): Promise<SessionTokens> => {
  const session = await getSession();

  if (!session) {
    throw new Error('Session expired. Please log in again.');
  }

  const accessToken = session.getAccessToken?.();
  const idToken = session.getIdToken?.();

  if (!accessToken || !idToken) {
    throw new Error('Invalid session. Please log in again.');
  }

  const jwtToken = accessToken.getJwtToken?.();
  const sessionId = idToken.payload?.sessionId;

  if (!jwtToken || !sessionId) {
    throw new Error('Session tokens missing. Please log in again.');
  }

  return { jwtToken, sessionId };
};

const LpiUploads: FC = () => {
  const { t } = useTranslation();
  const { getSession } = useContext(AccountContext);
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab state
  const [activeTab, setActiveTab] = useState<UploadTab>(() => {
    const tab = searchParams.get('tab');
    if (tab === 'ingestion-issues') return 'ingestion-issues';
    if (tab === 'upload-history') return 'upload-history';
    if (tab === 'datasets') return 'datasets';
    return 'upload-data';
  });

  // Ingestion issues state
  const initialErrorType = searchParams.get('errorType') || '';
  const urlStartDate = searchParams.get('startDate');
  const urlEndDate = searchParams.get('endDate');

  const [ingestionStartDate, setIngestionStartDate] = useState<Date | null>(
    () => urlStartDate ? parseDate(urlStartDate) : subDays(new Date(), 30)
  );
  const [ingestionEndDate, setIngestionEndDate] = useState<Date | null>(
    () => urlEndDate ? parseDate(urlEndDate) : new Date()
  );
  const [fetchTrigger, setFetchTrigger] = useState<number>(0);

  const getSessionTokensFn = useCallback(
    () => getSessionTokens(getSession),
    [getSession]
  );

  // Auto-fetch when navigated with URL params
  useEffect(() => {
    if (activeTab === 'ingestion-issues' && ingestionStartDate && ingestionEndDate && fetchTrigger === 0) {
      setFetchTrigger((prev) => prev + 1);
    }
  }, [activeTab, ingestionStartDate, ingestionEndDate, fetchTrigger]);

  const handleTabChange = (tab: UploadTab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(searchParams);
    if (tab === 'upload-data') {
      params.delete('tab');
      params.delete('errorType');
      params.delete('startDate');
      params.delete('endDate');
      params.delete('ds');
    } else {
      params.set('tab', tab);
    }
    setSearchParams(params);
  };

  const handleIngestionApply = () => {
    setFetchTrigger((prev) => prev + 1);
  };

  // Upload state
  const [files, setFiles] = useState<UploadFileMetadata[]>([]);
  const [uploadError, setUploadError] = useState<UploadError | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUploadSuccessful, setIsUploadSuccessful] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [uploadResults, setUploadResults] = useState<UploadResponse[]>([]);
  const [fileUploadProgress, setFileUploadProgress] = useState<number>(0); // 0-100 for current file
  const [isCancelling, setIsCancelling] = useState<boolean>(false);

  // AbortController ref for cancelling uploads
  const abortControllerRef = useRef<AbortController | null>(null);

  // Upload options
  const [checkDuplicate, setCheckDuplicate] = useState<boolean>(true);
  const [autoRecalculateCpi] = useState<boolean>(false);

  const hasFiles = files.length > 0;
  const allFilesDetected = files.every((f) => f.detection && !f.isDetecting);
  const hasUnknownFiles = files.some((f) => f.detection?.type === 'unknown');

  // Auto-clear success message after 10 seconds
  useEffect(() => {
    if (!isUploadSuccessful) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setIsUploadSuccessful(false);
      setUploadResults([]);
    }, 60000);

    return () => clearTimeout(timer);
  }, [isUploadSuccessful]);

  const clearFiles = () => {
    setFiles([]);
    setUploadResults([]);
  };

  const removeFile = (fileName: string) => {
    setFiles((prev) => prev.filter((f) => f.file.name !== fileName));
  };

  const handleFileTypeChange = (fileName: string, newType: DetectedFileType) => {
    setFiles((prev) =>
      prev.map((f) =>
        f.file.name === fileName ? { ...f, selectedType: newType } : f
      )
    );
  };

  const validateFile = (file: File): boolean => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const isValidExtension = fileExtension
      ? ALLOWED_EXTENSIONS.includes(fileExtension)
      : false;

    if (!isValidExtension) {
      const error = parseUploadError({ message: `File "${file.name}" type not supported. Only CSV, XLSX, and XLS files are allowed.` });
      setUploadError(error);
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      const error = parseUploadError({ message: `File "${file.name}" is larger than 50MB` });
      setUploadError(error);
      return false;
    }

    return true;
  };

  const validateFiles = (filesToValidate: File[]): File[] => {
    const validFiles: File[] = [];
    filesToValidate.some((file) => {
      if (!validateFile(file)) {
        return true;
      }
      validFiles.push(file);
      return false;
    });
    return validFiles;
  };

  // Detect file type using the /upload/detect endpoint
  const detectFileType = async (file: File, jwtToken: string, sessionId: string): Promise<FileDetectionResult | null> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${process.env.REACT_APP_LPI_API_URL}/upload/detect`,
        formData,
        {
          headers: {
            Authorization: `Session ${jwtToken}`,
            'X-Session-Id': sessionId,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data?.success && response.data?.detection) {
        return response.data.detection as FileDetectionResult;
      }
      return null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error detecting file type:', error);
      return null;
    }
  };

  // Detect types for all files - uses Promise.allSettled to avoid race conditions
  const detectAllFileTypes = async (filesToDetect: UploadFileMetadata[]) => {
    // Create unique IDs for each file to track them reliably
    const fileIds = filesToDetect.map((f) => `${f.file.name}-${f.file.size}-${f.file.lastModified}`);

    // Mark all files as detecting
    setFiles((prev) =>
      prev.map((f) => {
        const fileId = `${f.file.name}-${f.file.size}-${f.file.lastModified}`;
        return fileIds.includes(fileId)
          ? { ...f, isDetecting: true, detectionError: undefined }
          : f;
      })
    );

    try {
      const { jwtToken, sessionId } = await getSessionTokens(getSession);

      // Run all detections in parallel and collect results
      const detectionPromises = filesToDetect.map(async (fileMetadata) => {
        const fileId = `${fileMetadata.file.name}-${fileMetadata.file.size}-${fileMetadata.file.lastModified}`;
        try {
          const detection = await detectFileType(fileMetadata.file, jwtToken, sessionId);
          return { fileId, detection, error: null };
        } catch (err) {
          return { fileId, detection: null, error: err };
        }
      });

      const results = await Promise.allSettled(detectionPromises);

      // Build a map of fileId -> detection result
      const detectionMap = new Map<string, { detection: FileDetectionResult | null; error: boolean }>();
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          detectionMap.set(result.value.fileId, {
            detection: result.value.detection,
            error: result.value.error !== null,
          });
        }
      });

      // Update state once with all results atomically
      setFiles((prev) =>
        prev.map((f) => {
          const fileId = `${f.file.name}-${f.file.size}-${f.file.lastModified}`;
          const result = detectionMap.get(fileId);

          if (result) {
            return {
              ...f,
              detection: result.detection || {
                type: 'unknown' as DetectedFileType,
                confidence: 'low' as const,
                message: 'Could not detect file type',
              },
              isDetecting: false,
              detectionError: result.detection ? undefined : 'Detection failed',
            };
          }
          return f;
        })
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error detecting file types:', error);
      // Mark all as failed
      setFiles((prev) =>
        prev.map((f) => {
          const fileId = `${f.file.name}-${f.file.size}-${f.file.lastModified}`;
          return fileIds.includes(fileId)
            ? { ...f, isDetecting: false, detectionError: 'Detection failed' }
            : f;
        })
      );
    }
  };

  const addFiles = useCallback(
    (newFiles: File[]) => {
      setUploadError(null);
      const validFiles = validateFiles(newFiles);

      if (validFiles.length > 0) {
        const filesWithMetadata: UploadFileMetadata[] = validFiles.map((file) => ({
          file,
          isDetecting: true,
        }));

        setFiles((prev) => [...prev, ...filesWithMetadata]);

        // Trigger detection for new files
        detectAllFileTypes(filesWithMetadata);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      addFiles(acceptedFiles);
    },
    [addFiles]
  );

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    addFiles(selectedFiles);
  };

  // Cancel upload handler
  const handleCancelUpload = () => {
    if (abortControllerRef.current) {
      setIsCancelling(true);
      abortControllerRef.current.abort();
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      const error = parseUploadError({ message: 'Please attach at least one file' });
      setUploadError(error);
      return;
    }

    if (!allFilesDetected) {
      const error = parseUploadError({ message: 'Please wait for file detection to complete' });
      setUploadError(error);
      return;
    }

    // Create new AbortController for this upload session
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setIsCancelling(false);
    setUploadError(null);
    setUploadProgress({ current: 0, total: files.length, currentFileName: '' });
    setFileUploadProgress(0);
    setUploadResults([]);

    const totalFiles = files.length;
    const results: UploadResponse[] = [];
    const failures: { name: string; reason: string }[] = [];
    let cancelled = false;

    try {
      const { jwtToken, sessionId } = await getSessionTokens(getSession);

      // Build query params
      const params = new URLSearchParams();
      if (checkDuplicate) params.append('checkDuplicate', 'true');
      if (autoRecalculateCpi) params.append('autoRecalculateCpi', 'true');

      const queryString = params.toString();
      const uploadUrl = `${process.env.REACT_APP_LPI_API_URL}/upload${queryString ? `?${queryString}` : ''}`;

      // Upload each file independently. A failure on one file (e.g. a 409
      // duplicate) must NOT abort the batch or discard the files that already
      // uploaded — collect per-file successes and failures, then report both.
      for (let i = 0; i < totalFiles; i += 1) {
        const fileMetadata = files[i];

        // Stop the batch if the user cancelled (keeping what already uploaded).
        if (abortControllerRef.current?.signal.aborted) {
          cancelled = true;
          break;
        }

        setUploadProgress({
          current: i + 1,
          total: totalFiles,
          currentFileName: fileMetadata.file.name,
        });
        setFileUploadProgress(0);

        const formData = new FormData();
        formData.append('file', fileMetadata.file);

        try {
          // eslint-disable-next-line no-await-in-loop
          const response = await axios.post<UploadResponse>(
            uploadUrl,
            formData,
            {
              headers: {
                Authorization: `Session ${jwtToken}`,
                'X-Session-Id': sessionId,
                'Content-Type': 'multipart/form-data',
              },
              timeout: UPLOAD_TIMEOUT_MS,
              signal: abortControllerRef.current?.signal,
              onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                  const percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                  );
                  setFileUploadProgress(percentCompleted);
                }
              },
            }
          );

          if (response.data?.success) {
            results.push(response.data);
          } else {
            failures.push({
              name: fileMetadata.file.name,
              reason: response.data?.message || 'upload failed',
            });
          }
        } catch (err: any) {
          // A cancel stops the whole batch (but keeps what already succeeded).
          if (axios.isCancel(err) || err.message === 'Upload cancelled') {
            cancelled = true;
            break;
          }
          // A duplicate (409) or any other single-file error is recorded and
          // the batch continues with the remaining files.
          if (err?.response?.status === 409) {
            failures.push({
              name: fileMetadata.file.name,
              reason: 'already uploaded in the last 30 days',
            });
          } else {
            failures.push({
              name: fileMetadata.file.name,
              reason:
                err?.response?.data?.message || err?.message || 'upload failed',
            });
          }
        }
      }

      // Commit every file that actually uploaded — never discard them.
      setUploadResults(results);

      if (cancelled) {
        setUploadError(
          parseUploadError({
            message: `Upload cancelled. ${results.length} of ${totalFiles} file(s) were uploaded.`,
          })
        );
        setIsUploadSuccessful(false);
      } else if (failures.length > 0) {
        const summary = failures
          .map((f) => `"${f.name}" — ${f.reason}`)
          .join('; ');
        setUploadError(
          parseUploadError({
            message: `${results.length} of ${totalFiles} file(s) uploaded. ${failures.length} could not be uploaded: ${summary}`,
          })
        );
        setIsUploadSuccessful(false);
      } else {
        setIsUploadSuccessful(true);
        setUploadError(null);
      }

      // Keep only the files that failed (so the user can retry them); remove
      // the ones that uploaded successfully.
      const failedNames = new Set(failures.map((f) => f.name));
      setFiles(files.filter((f) => failedNames.has(f.file.name)));
    } catch (error: any) {
      // Handle cancelled upload at top level
      if (axios.isCancel(error) || error.message === 'Upload cancelled') {
        const parsedError = parseUploadError({ message: 'Upload was cancelled' });
        setUploadError(parsedError);
      } else {
        const parsedError = parseUploadError(error);
        setUploadError(parsedError);
      }
      setIsUploadSuccessful(false);
    } finally {
      setIsLoading(false);
      setUploadProgress(null);
      setFileUploadProgress(0);
      setIsCancelling(false);
      abortControllerRef.current = null;
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    multiple: true,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
  });

  const getConfidenceBadge = (confidence?: string) => {
    if (!confidence) return null;
    const styles = CONFIDENCE_STYLES[confidence as keyof typeof CONFIDENCE_STYLES] || CONFIDENCE_STYLES.low;
    return (
      <span
        className="lpi-uploads__confidence-badge"
        style={{ color: styles.color, backgroundColor: styles.bgColor }}
      >
        {confidence}
      </span>
    );
  };

  return (
    <div className="lpi-uploads container">
      {/* Tab Bar */}
      <div className="lpi-uploads__tab-bar">
        <button
          type="button"
          className={`lpi-uploads__tab ${activeTab === 'upload-data' ? 'lpi-uploads__tab--active' : ''}`}
          onClick={() => handleTabChange('upload-data')}
        >
          {t('Upload Data')}
        </button>
        <button
          type="button"
          className={`lpi-uploads__tab ${activeTab === 'upload-history' ? 'lpi-uploads__tab--active' : ''}`}
          onClick={() => handleTabChange('upload-history')}
        >
          {t('Upload History')}
        </button>
        <button
          type="button"
          className={`lpi-uploads__tab ${activeTab === 'ingestion-issues' ? 'lpi-uploads__tab--active' : ''}`}
          onClick={() => handleTabChange('ingestion-issues')}
        >
          {t('Ingestion Issues')}
        </button>
        <button
          type="button"
          className={`lpi-uploads__tab ${activeTab === 'datasets' ? 'lpi-uploads__tab--active' : ''}`}
          onClick={() => handleTabChange('datasets')}
        >
          {t('Source data')}
        </button>
      </div>

      {/* Upload Data Tab */}
      {activeTab === 'upload-data' && (
      <>
      <div className="lpi-uploads__header">
        <h1>{t('uploads.title')}</h1>
        <p className="lpi-uploads__subtitle">{t('uploads.description')}</p>
      </div>
      <div className="lpi-uploads__card">
        {/* Upload Progress Overlay */}
        {isLoading && uploadProgress && (
          <div className="lpi-uploads__progress-overlay">
            <div className="lpi-uploads__progress-card">
              <div className="lpi-uploads__progress-icon">
                <svg className="lpi-uploads__progress-spinner" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 11-6.219-8.56" />
                </svg>
              </div>
              <h3 className="lpi-uploads__progress-title">
                {isCancelling ? t('uploads.cancelling') : t('Uploading files...')}
              </h3>
              <p className="lpi-uploads__progress-file">{uploadProgress.currentFileName}</p>

              {/* Per-file progress bar */}
              <div className="lpi-uploads__progress-bar-container">
                <div
                  className="lpi-uploads__progress-bar"
                  style={{ width: `${fileUploadProgress}%` }}
                />
              </div>
              <p className="lpi-uploads__progress-percent">{fileUploadProgress}%</p>

              {/* Overall progress */}
              <p className="lpi-uploads__progress-text">
                {t('File')} {uploadProgress.current} {t('of')} {uploadProgress.total}
              </p>

              {/* Cancel button */}
              {!isCancelling && (
                <button
                  type="button"
                  className="lpi-uploads__cancel-btn"
                  onClick={handleCancelUpload}
                >
                  {t('uploads.cancelUpload')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Dropzone */}
        <div className={`lpi-uploads__dropzone ${isDragActive ? 'lpi-uploads__dropzone--active' : ''}`}>
          <div className="lpi-uploads__dropzone-inner" {...getRootProps()}>
            <input
              {...getInputProps()}
              id="file-drop-upload"
              className="lpi-uploads__dropzone-input"
              disabled={isLoading}
              data-testid="file-drop-upload"
              aria-label={t('Upload files')}
            />
            <div className="lpi-uploads__dropzone-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17,8 12,3 7,8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            {isDragActive ? (
              <p className="lpi-uploads__dropzone-text">{t('Drop files here')}</p>
            ) : (
              <>
                <p className="lpi-uploads__dropzone-text">{t('Drag & drop files here')}</p>
                <p className="lpi-uploads__dropzone-hint">{t('or click Browse to select files')}</p>
              </>
            )}
          </div>
        </div>

        {/* Browse Button */}
        <div className="lpi-uploads__browse-section">
          <label htmlFor="file-upload" className="lpi-uploads__browse-label">
            <input
              type="file"
              id="file-upload"
              className="lpi-uploads__browse-input"
              onChange={onChange}
              accept=".csv,.xlsx,.xls"
              multiple
              onClick={(event: React.MouseEvent<HTMLInputElement>) => {
                const { currentTarget } = event;
                currentTarget.value = '';
              }}
              disabled={isLoading}
              data-testid="file-upload-input"
            />
            <span className="btn btn-outline-primary lpi-uploads__browse-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
              {t('Browse Files')}
            </span>
          </label>
          <span className="lpi-uploads__file-types">{t('CSV, XLSX, XLS up to 50MB')}</span>
        </div>

        {/* Upload Options */}
        <div className="lpi-uploads__options">
          <label htmlFor="check-duplicate" className="lpi-uploads__option">
            <input
              id="check-duplicate"
              type="checkbox"
              checked={checkDuplicate}
              onChange={(e) => setCheckDuplicate(e.target.checked)}
              disabled={isLoading}
            />
            <span>{t('uploads.checkDuplicate')}</span>
          </label>
          {/* Checkbox removed — CPI recalculation is always queued in background after fuel upload */}
          {/* <label htmlFor="auto-recalculate-cpi" className="lpi-uploads__option">
            <input
              id="auto-recalculate-cpi"
              type="checkbox"
              checked={autoRecalculateCpi}
              onChange={(e) => setAutoRecalculateCpi(e.target.checked)}
              disabled={isLoading}
            />
            <span>{t('uploads.autoRecalculateCpi')}</span>
          </label> */}
        </div>

        {/* File Type Info */}
        <div className="lpi-uploads__file-order-info">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
          <span>{t('uploads.autoDetectInfo')}</span>
        </div>

        {/* Selected Files List */}
        {hasFiles && (
          <div className="lpi-uploads__file-list">
            <div className="lpi-uploads__file-list-header">
              <span className="lpi-uploads__file-count">
                {files.length} {files.length === 1 ? t('file') : t('files')} {t('selected')}
              </span>
              <button
                type="button"
                className="lpi-uploads__clear-btn"
                onClick={clearFiles}
                disabled={isLoading}
              >
                {t('Clear all')}
              </button>
            </div>
            <ul className="lpi-uploads__files">
              {files.map((fileMetadata) => (
                <li key={fileMetadata.file.name} className="lpi-uploads__file-item">
                  <div className="lpi-uploads__file-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14,2 14,8 20,8" />
                    </svg>
                  </div>
                  <div className="lpi-uploads__file-info">
                    <span className="lpi-uploads__file-name">{fileMetadata.file.name}</span>
                    <span className="lpi-uploads__file-meta">
                      {(fileMetadata.file.size / 1024 / 1024).toFixed(2)} MB
                      {fileMetadata.detection && !fileMetadata.isDetecting && (
                        <span className="lpi-uploads__confidence">
                          {getConfidenceBadge(fileMetadata.detection.confidence)}
                        </span>
                      )}
                    </span>
                    {fileMetadata.isDetecting && (
                      <span className="lpi-uploads__detecting">
                        <span className="lpi-uploads__detecting-spinner" />
                        {t('uploads.detecting')}
                      </span>
                    )}
                    {fileMetadata.detectionError && (
                      <span className="lpi-uploads__detection-error">
                        {t('uploads.detectionFailed')}
                      </span>
                    )}
                  </div>
                  {fileMetadata.detection && !fileMetadata.isDetecting && (
                    <DetectedFileTypeDropdown
                      value={fileMetadata.selectedType || fileMetadata.detection.type}
                      onChange={(type) => handleFileTypeChange(fileMetadata.file.name, type)}
                      disabled={isLoading}
                      showModule
                    />
                  )}
                  {fileMetadata.isDetecting && (
                    <div className="lpi-uploads__dropdown-placeholder">
                      <span className="lpi-uploads__detecting-spinner" />
                    </div>
                  )}
                  <button
                    type="button"
                    className="lpi-uploads__file-remove"
                    onClick={() => removeFile(fileMetadata.file.name)}
                    disabled={isLoading}
                    aria-label={t('Remove file')}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Warning for unknown files */}
        {hasUnknownFiles && (
          <div className="lpi-uploads__warning">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>{t('uploads.unknownFileWarning')}</span>
          </div>
        )}

        {/* Error Display */}
        {uploadError && (
          <UploadErrorDisplay
            error={uploadError}
            onDismiss={() => setUploadError(null)}
            onRetry={handleUpload}
            onClearFile={clearFiles}
            onTryDifferentFile={() => {
              clearFiles();
              document.getElementById('file-upload')?.click();
            }}
            onRefreshPage={() => window.location.reload()}
          />
        )}

        {/* Success Message */}
        {isUploadSuccessful && (
          <div className="lpi-uploads__success">
            <button
              type="button"
              className="lpi-uploads__success-close"
              onClick={() => {
                setIsUploadSuccessful(false);
                setUploadResults([]);
              }}
              aria-label={t('Close')}
            >
              &times;
            </button>
            <div className="lpi-uploads__success-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22,4 12,14.01 9,11.01" />
              </svg>
            </div>
            <div className="lpi-uploads__success-content">
              <h3>{t('Files uploaded successfully!')}</h3>
              <p>{t('uploads.successMessage')}</p>

              {/* Upload Results Summary */}
              {uploadResults.length > 0 && (
                <div className="lpi-uploads__results-summary">
                  {uploadResults.map((result) => (
                    <div key={result.filename} className="lpi-uploads__result-item">
                      <span className="lpi-uploads__result-filename">{result.filename}</span>
                      <span className="lpi-uploads__result-type">
                        {t(FILE_TYPE_LABELS[result.fileType])}
                      </span>
                      {getConfidenceBadge(result.detection?.confidence)}
                      {result.processing?.status && (
                        <span className={`lpi-uploads__result-status lpi-uploads__result-status--${result.processing.status}`}>
                          {result.processing.status}
                        </span>
                      )}
                    </div>
                  ))}
                  {uploadResults[uploadResults.length - 1]?.processing?.status === 'queued' && (
                    <div className="lpi-uploads__processing-message">
                      {uploadResults[uploadResults.length - 1]?.message && (
                        <div>{uploadResults[uploadResults.length - 1].message}</div>
                      )}
                      <div className="lpi-uploads__queued-info">
                        {t('uploads.queueMessageWithLpi')}
                      </div>
                    </div>
                  )}
                  {uploadResults[uploadResults.length - 1]?.processing?.status !== 'queued' && uploadResults[uploadResults.length - 1]?.message && (
                    <div className="lpi-uploads__processing-message">
                      {uploadResults[uploadResults.length - 1].message}
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
        )}

        {/* Upload Button */}
        <div className="lpi-uploads__actions">
          <button
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={isLoading || !hasFiles || !allFilesDetected || isUploadSuccessful}
            data-testid="upload-button"
            type="button"
          >
            {isLoading ? (
              <>
                <span className="lpi-uploads__btn-spinner" />
                {t('Uploading...')}
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17,8 12,3 7,8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                {t('Upload Files')}
              </>
            )}
          </button>
        </div>
      </div>
      </>
      )}

      {/* Upload History Tab */}
      {activeTab === 'upload-history' && (
        <UploadHistoryTab getSessionTokensFn={getSessionTokensFn} />
      )}

      {/* Ingestion Issues Tab */}
      {activeTab === 'ingestion-issues' && (
        <div className="lpi-uploads__ingestion-issues">
          <div className="lpi-uploads__header">
            <h1>{t('Ingestion Issues')}</h1>
            <p className="lpi-uploads__subtitle">{t('Review data quality issues from uploaded files')}</p>
          </div>
          <div className="lpi-uploads__ingestion-controls">
            <DateRangeDropdown
              startDate={ingestionStartDate}
              endDate={ingestionEndDate}
              onStartDateChange={setIngestionStartDate}
              onEndDateChange={setIngestionEndDate}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleIngestionApply}
              disabled={!ingestionStartDate || !ingestionEndDate}
            >
              {t('Apply')}
            </button>
          </div>
          <IngestionIssuesTable
            startDate={ingestionStartDate}
            endDate={ingestionEndDate}
            getSessionTokensFn={getSessionTokensFn}
            formatDateToStringFn={formatDateToString}
            fetchTrigger={fetchTrigger}
            initialErrorType={initialErrorType}
          />
        </div>
      )}

      {/* Datasets Tab */}
      {activeTab === 'datasets' && (
        <DatasetsTab getSessionTokensFn={getSessionTokensFn} />
      )}
    </div>
  );
};

export default LpiUploads;
